import { CheckCircle, Circle } from "@phosphor-icons/react";

/**
 * SectionProgressMeter — subtle 8-section completion indicator shown at
 * the top of the résumé editor. Each section flips from "empty" to
 * "complete" once meaningful data is present. Meant to nudge users to
 * finish without being noisy or blocking.
 *
 * Completion rules (kept generous — one meaningful entry is enough):
 *   Personal        →  fullName + (email OR phone)
 *   Summary         →  ≥ 20 characters
 *   Experience      →  ≥ 1 entry with company AND role
 *   Education       →  ≥ 1 entry with school AND degree
 *   Skills          →  ≥ 3 non-empty skills
 *   Projects        →  ≥ 1 entry with a name
 *   Certifications  →  ≥ 1 entry with a name
 *   Languages       →  ≥ 1 entry with a name
 */
const SECTIONS = [
  { id: "personal", label: "Personal" },
  { id: "summary", label: "Summary" },
  { id: "experience", label: "Experience" },
  { id: "education", label: "Education" },
  { id: "skills", label: "Skills" },
  { id: "projects", label: "Projects" },
  { id: "certifications", label: "Certifications" },
  { id: "languages", label: "Languages" },
];

const isComplete = (id, r) => {
  if (!r) return false;
  switch (id) {
    case "personal": {
      const p = r.personal || {};
      return Boolean(p.fullName?.trim() && (p.email?.trim() || p.phone?.trim()));
    }
    case "summary":
      return (r.summary || "").trim().length >= 20;
    case "experience":
      return (r.experience || []).some((e) => e.company?.trim() && e.role?.trim());
    case "education":
      return (r.education || []).some((e) => e.school?.trim() && e.degree?.trim());
    case "skills":
      return (r.skills || []).filter((s) => (s || "").trim()).length >= 3;
    case "projects":
      return (r.projects || []).some((p) => p.name?.trim());
    case "certifications":
      return (r.certifications || []).some((c) => c.name?.trim());
    case "languages":
      return (r.languages || []).some((l) => l.name?.trim());
    default:
      return false;
  }
};

export default function SectionProgressMeter({ resume }) {
  const statuses = SECTIONS.map((s) => ({ ...s, done: isComplete(s.id, resume) }));
  const completed = statuses.filter((s) => s.done).length;
  const total = statuses.length;
  const pct = Math.round((completed / total) * 100);
  const allDone = completed === total;

  return (
    <div
      className="mb-5 border border-black/10 bg-white p-3"
      data-testid="section-progress-meter"
      aria-label={`${completed} of ${total} résumé sections complete`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="label-mono">Résumé progress</div>
        <div
          className={`font-mono text-[11px] tabular-nums ${allDone ? "text-[#2400FF] font-semibold" : "text-neutral-600"}`}
          data-testid="section-progress-count"
        >
          {completed}/{total} · {pct}%
        </div>
      </div>
      <div className="h-1.5 w-full bg-neutral-100 overflow-hidden rounded-sm">
        <div
          className={`h-full transition-[width] duration-500 ease-out ${allDone ? "bg-[#2400FF]" : "bg-neutral-950"}`}
          style={{ width: `${pct}%` }}
          data-testid="section-progress-bar"
        />
      </div>
      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5">
        {statuses.map((s) => (
          <span
            key={s.id}
            className={`inline-flex items-center gap-1 text-[11px] transition-colors ${
              s.done ? "text-neutral-900" : "text-neutral-400"
            }`}
            data-testid={`section-progress-item-${s.id}`}
            data-done={s.done ? "1" : "0"}
          >
            {s.done ? (
              <CheckCircle size={13} weight="fill" className="text-[#2400FF]" />
            ) : (
              <Circle size={13} weight="regular" />
            )}
            <span>{s.label}</span>
          </span>
        ))}
      </div>
      {allDone && (
        <div className="mt-2 text-[11px] text-[#2400FF] font-medium" data-testid="section-progress-done-msg">
          All sections complete — you're ready to download.
        </div>
      )}
    </div>
  );
}
